import React, { useEffect, useState, useCallback } from 'react';
import { ShieldCheck, AlertTriangle, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';
import { Card } from '../../components/ui/Card';
import { PageLoader } from '../../components/crm/ui';

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-5 border-l-4 shadow-[0_1px_3px_rgba(0,0,0,0.08)]" style={{ borderLeftColor: accent }}>
      <p className="text-[11px] font-bold text-[#706E6B] uppercase mb-1">{label}</p>
      <h3 className="text-2xl font-bold text-[#080707] tabular-nums">{value}</h3>
    </div>
  );
}

const fmt = (n, currency = 'INR') => new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(n || 0);

// Cross-checks every successful Payment against its linked Invoice, and
// every "paid" Invoice against a real successful Payment — the two records
// /subscriptions/create-order writes up front for every plan purchase. Any
// row shown here is a real mismatch worth a human looking at, not just a
// display glitch.
export default function SuperAdminReconciliation() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/reconciliation', { params: { days } });
      setData(data);
    } catch (e) {
      alertService.error('Load Failed', e.response?.data?.error || 'Failed to run reconciliation.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={ShieldCheck}
        title="Settlement Reconciliation"
        subtitle="Cross-checks payment transactions against invoices to surface anything that doesn't line up."
      />

      <div className="flex items-center gap-3">
        <select
          className="border border-[#D8DDE6] rounded-[4px] px-3 h-9 text-[13px] focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] outline-none"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last 365 days</option>
        </select>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded whitespace-nowrap text-[13px] font-semibold bg-white border border-[#C9C9C9] text-[#706E6B] hover:bg-[#F4F6F9] transition-colors disabled:opacity-60"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading && !data ? (
        <PageLoader inline />
      ) : data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Successful Payments" value={data.summary.successPaymentCount} accent="#0176D3" />
            <StatCard label="Paid Invoices" value={data.summary.paidInvoiceCount} accent="#2E844A" />
            <StatCard label="Amount Difference" value={fmt(data.summary.amountDifference)} accent={data.summary.amountDifference === 0 ? '#2E844A' : '#BA0517'} />
            <StatCard label="Mismatches" value={data.summary.mismatchCount} accent={data.summary.mismatchCount === 0 ? '#2E844A' : '#BA0517'} />
          </div>

          {data.summary.mismatchCount === 0 ? (
            <div className="bg-white border border-[#E5E5E5] rounded-lg p-10 text-center shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
              <ShieldCheck size={32} className="mx-auto text-[#2E844A] mb-2" />
              <p className="text-[13px] text-[#080707] font-semibold">Everything reconciles — no mismatches in this period.</p>
            </div>
          ) : (
            <>
              {data.paymentsWithoutPaidInvoice.length > 0 && (
                <Card className="p-0 overflow-hidden">
                  <div className="px-5 py-4 border-b border-[#D8DDE6] flex items-center gap-2">
                    <AlertTriangle size={15} className="text-[#BA0517]" />
                    <h2 className="text-[13px] font-bold text-[#080707]">Payment succeeded, but its invoice isn't marked paid</h2>
                  </div>
                  <div className="slds2-table-wrap overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-2.5">Company</th>
                        <th className="text-left px-4 py-2.5">Transaction ID</th>
                        <th className="text-right px-4 py-2.5">Amount</th>
                        <th className="text-left px-4 py-2.5">Invoice</th>
                        <th className="text-left px-4 py-2.5">Paid On</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D8DDE6]">
                      {data.paymentsWithoutPaidInvoice.map((p) => (
                        <tr key={p._id}>
                          <td className="px-4 py-2.5 font-semibold text-[#080707]">{p.companyId?.companyName || '—'}</td>
                          <td className="px-4 py-2.5 font-mono text-[12px]">{p.transactionId}</td>
                          <td className="px-4 py-2.5 text-right">{fmt(p.amount, p.currency)}</td>
                          <td className="px-4 py-2.5">{p.invoiceId ? `${p.invoiceId.invoiceNumber} (${p.invoiceId.status})` : 'No invoice linked'}</td>
                          <td className="px-4 py-2.5">{new Date(p.createdAt).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </Card>
              )}

              {data.invoicesWithoutSuccessPayment.length > 0 && (
                <Card className="p-0 overflow-hidden">
                  <div className="px-5 py-4 border-b border-[#D8DDE6] flex items-center gap-2">
                    <AlertTriangle size={15} className="text-[#BA0517]" />
                    <h2 className="text-[13px] font-bold text-[#080707]">Invoice marked paid, but no successful payment found</h2>
                  </div>
                  <div className="slds2-table-wrap overflow-x-auto">
                  <table className="w-full text-[13px]">
                    <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
                      <tr>
                        <th className="text-left px-4 py-2.5">Company</th>
                        <th className="text-left px-4 py-2.5">Invoice #</th>
                        <th className="text-right px-4 py-2.5">Amount</th>
                        <th className="text-left px-4 py-2.5">Marked Paid</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#D8DDE6]">
                      {data.invoicesWithoutSuccessPayment.map((inv) => (
                        <tr key={inv._id}>
                          <td className="px-4 py-2.5 font-semibold text-[#080707]">{inv.companyId?.companyName || '—'}</td>
                          <td className="px-4 py-2.5 font-mono text-[12px]">{inv.invoiceNumber}</td>
                          <td className="px-4 py-2.5 text-right">{fmt(inv.amount?.totalAmount, inv.amount?.currency)}</td>
                          <td className="px-4 py-2.5">{new Date(inv.updatedAt).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
